import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import './FlipCountdown.scss';
    
const getTimeLeft = targetMs => {
    const diff = Math.max(0, targetMs - Date.now());
    return {
        days:    Math.floor(diff / 86400000),
        hours:   Math.floor((diff % 86400000) / 3600000),
        minutes: Math.floor((diff % 3600000)  / 60000),
        seconds: Math.floor((diff % 60000)    / 1000),
        done:    diff <= 0,
    };
};

// Modeled after FlipDown's 4-layer structure:
// - card-top  (static, always present, shows NEXT value — hidden behind leaf-f)
// - card-bot  (static, always present, shows CURRENT value — visible at bottom)
// - leaf      (animated, rotateX 0 → -180deg)
//   - leaf-f  (front face, CURRENT value, backface-visibility: hidden)
//   - leaf-r  (rear face, NEXT value, pre-rotated -180deg, backface-visibility: hidden)
//
// The flip "brings" the change: next value only becomes visible as leaf-f folds away.
const FlipDigit = ({ digit }) => {
    const val = String(digit);
    const [face, setFace]       = useState(val); // committed/current value
    const [next, setNext]       = useState(val); // incoming value
    const [flipping, setFlipping] = useState(false);

    useLayoutEffect(() => {
        if (val === face || flipping) return;
        setNext(val);
        setFlipping(true);
        const id = setTimeout(() => {
            setFace(val);
            setFlipping(false);
        }, 500);
        return () => clearTimeout(id);
    }, [val]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div className="spk-fc-card">
            <div className="spk-fc-card-top"><span>{next}</span></div>
            <div className="spk-fc-card-bot"><span>{face}</span></div>
            <div className={`spk-fc-leaf${flipping ? ' spk-fc-leaf--flip' : ''}`}>
                <div className="spk-fc-leaf-f"><span>{face}</span></div>
                <div className="spk-fc-leaf-r"><span>{next}</span></div>
            </div>
        </div>
    );
};

const FlipUnit = ({ value, label, minDigits = 2 }) => {
    const s = String(value).padStart(minDigits, '0');
    return (
        <div className="spk-fc-unit">
            <div className="spk-fc-digits">
                {s.split('').map((d, i) => <FlipDigit key={i} digit={parseInt(d)} />)}
            </div>
            <span className="spk-fc-label">{label}</span>
        </div>
    );
};

const FlipCountdown = ({ targetUtc, onEnd }) => {
    const targetMs  = useRef(Date.parse(targetUtc));
    const [time, setTime] = useState(() => getTimeLeft(targetMs.current));
    // Fixed at mount so digit count never changes mid-countdown (avoids remounting FlipDigits)
    const dayDigits = useRef(Math.max(2, String(getTimeLeft(targetMs.current).days).length));

    useEffect(() => {
        if (isNaN(targetMs.current) || time.done) return;

        const tick = () => {
            const t = getTimeLeft(targetMs.current);
            setTime(t);
            if (t.done) { clearInterval(id); onEnd?.(); }
        };

        const id = setInterval(tick, 1000);

        const onVisible = () => { if (!document.hidden) tick(); };
        document.addEventListener('visibilitychange', onVisible);

        return () => {
            clearInterval(id);
            document.removeEventListener('visibilitychange', onVisible);
        };
    }, []);

    if (isNaN(targetMs.current)) return null;

    if (time.done) {
        const elapsedMin = Math.floor((Date.now() - targetMs.current) / 60000);
        const elapsedText = elapsedMin < 1 ? 'Just started' : `Started ${elapsedMin} min ago`;
        return (
            <div className="spk-fc-live">
                <span className="spk-fc-live__dot" />
                <div className="spk-fc-live__text">
                    <span className="spk-fc-live__badge">Live Now</span>
                    <span className="spk-fc-live__sub">{elapsedText}</span>
                </div>
            </div>
        );
    }

    return (
        <div className="spk-fc">
            <FlipUnit value={time.days}    label="Days" minDigits={dayDigits.current} />
            <span className="spk-fc-sep" aria-hidden="true" style={{ visibility: 'hidden' }}>:</span>
            <FlipUnit value={time.hours}   label="Hrs"  />
            <span className="spk-fc-sep">:</span>
            <FlipUnit value={time.minutes} label="Min"  />
            <span className="spk-fc-sep">:</span>
            <FlipUnit value={time.seconds} label="Sec"  />
        </div>
    );
};

export default FlipCountdown;
